import React, { useContext, useEffect, useMemo } from 'react';
import ReactGA from 'react-ga4';
import { MenuPrintConfig, MenuPrintPageProps } from './types';
import { UserVesselsProvider } from '../../../contexts/userVessels';
import { SetupPrintPageStyles, useColumns, usePrintConfigFromURLParams, useTaps } from './utils';
import { SessionContext } from '../../../contexts/session';

export interface NewYorkPageProps extends MenuPrintPageProps {
  originalColumnCount: number;
}

interface NewYorkMenuPrintDocumentProps {
  Page: React.FC<NewYorkPageProps>;
}

/**
 * Variant of MenuPrintDocument that forces columnCount=1 at the framework
 * level so all data flows through a single column per page. The original
 * user-chosen columnCount is forwarded as `originalColumnCount` so the
 * page's column component can build its own internal grid.
 */
const NewYorkMenuPrintDocument: React.FC<NewYorkMenuPrintDocumentProps> = ({ Page }) => {
  const urlConfig = usePrintConfigFromURLParams(window.location.search);
  const originalColumnCount = urlConfig.columnCount;

  const config: MenuPrintConfig = useMemo(() => ({ ...urlConfig, columnCount: 1 }), [urlConfig]);

  const taps = useTaps(config);
  const { columns, onColumnRef } = useColumns(taps, config);
  const { account } = useContext(SessionContext);

  let pages: any[][] = [];
  for (let i = 0; i < columns.length; i++) {
    if (i % config.columnCount === 0) {
      pages.push([Array(config.columnCount).fill(undefined)]);
    }
    let p = pages[pages.length - 1];
    p[i % config.columnCount] = columns[i];
  }

  useEffect(() => {
    if (taps.length && columns.length) {
      const timer = setTimeout(() => {
        const paramsObject = JSON.stringify(urlConfig);

        const gaLabel = `Account: ${account._id} Template: New York  Custom Params: ${paramsObject}`;

        ReactGA.event({
          category: 'print_start',
          action: 'print_start',
          label: gaLabel
        });
        // @ts-ignore
        if (typeof window.gtag === 'function') {
          // @ts-ignore
          window.gtag('event', 'print_start', {
            event_category: 'print_start',
            event_label: gaLabel
          });
        }

        window.print();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [account?._id, columns, urlConfig, taps]);

  if (!urlConfig.premium) {
    return null;
  }

  return (
    <UserVesselsProvider>
      {pages.map((page, i) => (
        <Page
          key={i}
          config={config}
          originalColumnCount={originalColumnCount}
          columns={page}
          onColumnRef={(el, colIndex) => onColumnRef(el, i, colIndex)}
        />
      ))}
      <SetupPrintPageStyles />
    </UserVesselsProvider>
  );
};

export default NewYorkMenuPrintDocument;
